import React from 'react';
import { ChevronUp, ChevronDown, Search, X } from 'lucide-react';

/**
 * Tablo kolon başlığı: sıralama + arama.
 * Aktif filtre başka kolona geçilince de chip olarak görünür kalır.
 */
const ColumnSearchHeaderCell = ({
  label,
  searchValue = '',
  isSearchActive = false,
  isSorted = false,
  sortDirection = 'asc',
  onSort,
  onOpenSearch,
  onClearSearch,
  onSearchChange,
  onCloseSearchEdit,
  align = 'left',
  as: Component = 'th',
  className = '',
  style,
  compact = false,
  showSort = true,
  showSearch = true
}) => {
  const labelLines = Array.isArray(label) ? label : [label];
  const labelText = labelLines.join(' ');
  const hasFilter = Boolean(String(searchValue || '').trim());

  const alignClass =
    align === 'center' ? 'text-center' : align === 'right' ? 'text-right' : 'text-left';
  const justifyClass =
    align === 'center' ? 'justify-center' : align === 'right' ? 'justify-end' : 'justify-start';

  const inputSizeClass = compact
    ? 'px-1.5 py-1 text-[11px]'
    : 'px-2 py-1 text-xs';

  const filterBg = hasFilter ? 'bg-emerald-50/80 dark:bg-emerald-900/20' : '';

  return (
    <Component
      className={`relative group ${alignClass} ${filterBg} ${className}`.trim()}
      style={style}
    >
      {isSearchActive ? (
        <div className={`flex items-center gap-1 w-full min-w-0 ${justifyClass}`}>
          <input
            type="text"
            value={searchValue}
            onChange={(e) => onSearchChange?.(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                onCloseSearchEdit?.();
              }
            }}
            autoFocus
            placeholder={`${labelText} ara...`}
            title={searchValue || `${labelText} ara...`}
            className={`w-full min-w-0 ${inputSizeClass} font-semibold normal-case tracking-normal border-2 border-emerald-500 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500`}
            onClick={(e) => e.stopPropagation()}
          />
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onClearSearch?.();
            }}
            className="flex-shrink-0 p-0.5 text-gray-500 hover:text-red-600 dark:hover:text-red-400"
            title="Filtreyi temizle"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      ) : hasFilter ? (
        <div className={`flex flex-col gap-0.5 min-w-0 ${alignClass}`}>
          <span className="text-[9px] uppercase tracking-tight text-gray-400 dark:text-gray-500 leading-none">
            {labelText}
          </span>
          <div className={`flex items-center gap-0.5 min-w-0 ${justifyClass}`}>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onOpenSearch?.();
              }}
              className="min-w-0 max-w-full truncate px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-800/60 text-emerald-800 dark:text-emerald-100 text-[11px] font-semibold normal-case tracking-normal border border-emerald-300 dark:border-emerald-600"
              title={`Filtre: ${searchValue} (düzenlemek için tıkla)`}
            >
              {searchValue}
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onClearSearch?.();
              }}
              className="flex-shrink-0 p-0.5 text-emerald-700 dark:text-emerald-300 hover:text-red-600"
              title="Filtreyi temizle"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        </div>
      ) : (
        <div className={`flex items-end gap-0.5 min-w-0 ${justifyClass}`}>
          <span
            className={`cursor-pointer hover:text-gray-700 dark:hover:text-gray-200 leading-tight ${alignClass}`}
            onClick={() => onSort?.()}
            title={labelText}
          >
            {labelLines.map((line, idx) => (
              <span key={`${labelText}-${idx}`} className="block whitespace-normal">
                {line}
              </span>
            ))}
          </span>
          {(showSort || showSearch) && (
            <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 pb-0.5">
              {showSort && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSort?.();
                  }}
                  className="p-0.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                  title="Sırala"
                >
                  {isSorted ? (
                    sortDirection === 'asc' ? (
                      <ChevronUp className="w-3 h-3" />
                    ) : (
                      <ChevronDown className="w-3 h-3" />
                    )
                  ) : (
                    <ChevronUp className="w-3 h-3 opacity-50" />
                  )}
                </button>
              )}
              {showSearch && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenSearch?.();
                  }}
                  className="p-0.5 text-gray-400 hover:text-green-600 dark:hover:text-green-400"
                  title="Ara"
                >
                  <Search className="w-3 h-3" />
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </Component>
  );
};

/** Aynı kolonda X ile temizle; başka kolona geçerken değeri koru */
export const openOrToggleColumnSearch = ({
  column,
  activeColumn,
  setActiveColumn,
  searches,
  setSearches,
  clearIfSame = true
}) => {
  if (activeColumn === column && clearIfSame) {
    setActiveColumn(null);
    setSearches((prev) => {
      const next = { ...prev };
      delete next[column];
      return next;
    });
    return;
  }
  setActiveColumn(column);
  if (searches[column] === undefined) {
    setSearches((prev) => ({ ...prev, [column]: '' }));
  }
};

export const clearColumnSearchValue = ({
  column,
  activeColumn,
  setActiveColumn,
  setSearches
}) => {
  setSearches((prev) => {
    const next = { ...prev };
    delete next[column];
    return next;
  });
  if (activeColumn === column) {
    setActiveColumn(null);
  }
};

export default ColumnSearchHeaderCell;
